/**
 * Summary Box Component
 * =====================
 * Displays key statistics and metadata for Excel files
 */

import React from 'react';

import {
  Layers,
  Rows,
  Columns,
  Type,
  Hash,
  Calendar,
  FileSpreadsheet,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';

export default function SummaryBox({ metadata, schema, analysis, intelligence }) {
  // Compile stats from various sources
  const stats = {
    sheetCount: metadata?.sheetCount || schema?.sheetCount || 0,
    totalRows: metadata?.totalRows || schema?.totalRows || 0,
    totalColumns: metadata?.totalColumns || schema?.totalColumns || 0,
    sheets: metadata?.sheets || schema?.sheets?.map(s => s.name) || [],
  };

  // Get data types summary from schema
  const dataTypes =
    schema?.sheets?.reduce((acc, sheet) => {
      sheet.columns?.forEach(col => {
        acc[col.type] = (acc[col.type] || 0) + 1;
      });
      return acc;
    }, {}) || {};

  // Get quality issues count
  const qualityIssues = analysis?.dataQuality?.issues?.length || 0;

  // Get key metrics from intelligence
  const keyMetrics = intelligence?.metrics?.slice(0, 4) || [];

  return (
    <div className="summary-box">
      {/* Main Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon sheets">
            <Layers className="w-5 h-5" />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.sheetCount}</span>
            <span className="stat-label">Sheets</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon rows">
            <Rows className="w-5 h-5" />
          </div>
          <div className="stat-content">
            <span className="stat-value">{formatNumber(stats.totalRows)}</span>
            <span className="stat-label">Rows</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon columns">
            <Columns className="w-5 h-5" />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.totalColumns}</span>
            <span className="stat-label">Columns</span>
          </div>
        </div>

        {qualityIssues > 0 && (
          <div className="stat-card warning">
            <div className="stat-icon issues">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div className="stat-content">
              <span className="stat-value">{qualityIssues}</span>
              <span className="stat-label">Issues</span>
            </div>
          </div>
        )}
      </div>

      {/* Sheet List */}
      {stats.sheets.length > 0 && (
        <div className="summary-section">
          <h4 className="section-title">
            <FileSpreadsheet className="w-4 h-4" />
            Sheets
          </h4>
          <div className="sheet-list">
            {stats.sheets.slice(0, 5).map((name, idx) => {
              const sheetInfo = schema?.sheets?.find(s => s.name === name);
              return (
                <div key={idx} className="sheet-item">
                  <span className="sheet-name">{name}</span>
                  {sheetInfo && (
                    <span className="sheet-meta">
                      {sheetInfo.rowCount} rows • {sheetInfo.columnCount} cols
                    </span>
                  )}
                </div>
              );
            })}
            {stats.sheets.length > 5 && (
              <div className="sheet-more">+{stats.sheets.length - 5} more</div>
            )}
          </div>
        </div>
      )}

      {/* Data Types */}
      {Object.keys(dataTypes).length > 0 && (
        <div className="summary-section">
          <h4 className="section-title">
            <Type className="w-4 h-4" />
            Column Types
          </h4>
          <div className="type-list">
            {Object.entries(dataTypes).map(([type, count]) => (
              <div key={type} className={`type-badge type-${type}`}>
                <span className="type-icon">{getTypeIcon(type)}</span>
                <span className="type-name">{type}</span>
                <span className="type-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Metrics */}
      {keyMetrics.length > 0 && (
        <div className="summary-section">
          <h4 className="section-title">
            <TrendingUp className="w-4 h-4" />
            Key Metrics
          </h4>
          <div className="metrics-list">
            {keyMetrics.map((metric, idx) => (
              <div key={idx} className="metric-item">
                <span className="metric-name">{metric.name}</span>
                <span className="metric-category">{metric.category}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data Quality */}
      {analysis?.dataQuality && (
        <div className="summary-section">
          <h4 className="section-title">
            <AlertCircle className="w-4 h-4" />
            Data Quality
          </h4>
          <div className="quality-summary">
            <div className={`quality-score grade-${analysis.dataQuality.grade?.toLowerCase()}`}>
              <span className="grade">{analysis.dataQuality.grade}</span>
              <span className="score">{analysis.dataQuality.score}%</span>
            </div>
            {analysis.dataQuality.issues?.length > 0 && (
              <div className="quality-issues">
                {analysis.dataQuality.issues.slice(0, 3).map((issue, idx) => (
                  <div key={idx} className={`issue-item severity-${issue.severity}`}>
                    <AlertCircle className="w-3 h-3" />
                    <span>{issue.message}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function formatNumber(num) {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num?.toString() || '0';
}

function getTypeIcon(type) {
  switch (type) {
    case 'integer':
    case 'float':
      return '123';
    case 'string':
      return 'Aa';
    case 'datetime':
      return '📅';
    case 'boolean':
      return '✓';
    default:
      return '?';
  }
}
